import { useEffect, useState } from 'react';

import { NumberInput } from '@/components/ui/NumberInput';
import { fineUnitFor, trimNumber } from '@/utils/units';

/**
 * Расход ингредиента с выбором единицы ввода.
 *
 * Позиция учитывается в килограммах, а повар думает в граммах: раньше в поле
 * набивали «1» при цене 654 ₽/кг и получали 654 ₽ на порцию — отсюда фудкост в
 * 3574%. Теперь для кг/л по умолчанию включён ввод в г/мл, а в базу всегда уходит
 * значение в базовой единице.
 */
export function QuantityField({
  value,
  unit,
  autoFocus,
  onChange,
  onEnter,
  startInBaseUnit = false,
}: {
  /** Значение в базовой единице позиции. */
  value: number;
  /** Базовая единица позиции (кг, л, шт). */
  unit: string;
  autoFocus?: boolean;
  onChange: (next: number) => void;
  onEnter?: () => void;
  /** Открывать в кг/л, а не в г/мл: выход заготовки задают литрами, «1» — это литр. */
  startInBaseUnit?: boolean;
}) {
  const fine = fineUnitFor(unit);
  // Мелкая единица по умолчанию: 150 г понятнее, чем 0,15 кг. Крупные расходы
  // (от одной базовой единицы) показываем как есть — «2 кг картошки».
  const prefersFine = () => !startInBaseUnit && !!fine && (value === 0 || value < 1);
  const [useFine, setUseFine] = useState(prefersFine);

  // Смена позиции в строке меняет единицу — режим ввода подстраиваем заново.
  useEffect(() => {
    setUseFine(prefersFine());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const factor = useFine && fine ? fine.factor : 1;
  const shownUnit = useFine && fine ? fine.unit : unit;
  const shownValue = Math.round(value * factor * 1000) / 1000;

  return (
    <div className="flex items-stretch gap-1">
      <div className="relative flex-1">
        <NumberInput
          className="input pr-10 text-right tabular-nums"
          value={shownValue}
          autoFocus={autoFocus}
          onChange={(n) => onChange(n / factor)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
          {shownUnit}
        </span>
      </div>
      {fine && (
        <button
          type="button"
          className="px-2 text-xs rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 shrink-0"
          onClick={() => setUseFine((v) => !v)}
          title={`Вводить в ${useFine ? unit : fine.unit}`}
        >
          {useFine ? unit : fine.unit}
        </button>
      )}
    </div>
  );
}

/** Подпись под строкой: «654 ₽/кг → 98,10 ₽ за 150 г». */
export function quantityHint(
  quantity: number,
  unit: string,
  costPerUnit: number,
  formatRub: (kop: number) => string,
): string {
  if (costPerUnit <= 0) return 'цена ингредиента не задана';
  const fine = fineUnitFor(unit);
  const asFine = fine && quantity > 0 && quantity < 1;
  const shown = asFine
    ? `${trimNumber(quantity * fine.factor)} ${fine.unit}`
    : `${trimNumber(quantity)} ${unit}`;
  return `${formatRub(costPerUnit)}/${unit} → ${formatRub(Math.round(quantity * costPerUnit))} за ${shown}`;
}
